import type { ReactElement, ReactNode } from 'react';
import { PublicationStatus, WITHDRAWN_NOTE, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { Link, useT } from '../../../../app/di';
import { IconButton } from '../../../../ui-kit/Button/Button';
import { PublicationLine } from '../../scheduling';
import styles from './PublicationHistory.module.scss';

export interface PublicationHistoryProps {
  blogId: string;
  timezone: string;
  publications: readonly PublicationDto[];
  /** PUB-UC-9: исправить вышедшую; без колбэка — только просмотр. */
  onEdit?(publication: PublicationDto): void;
  /** PUB-UC-10: удалить вышедшую; без колбэка — только просмотр. */
  onDelete?(publication: PublicationDto): void;
  /** Идёт удаление: действия заблокированы. */
  pending?: boolean;
  /** Публикация, которую правят, и форма правки под ней. */
  editing?: { publicationId: string; form: ReactNode } | null;
}

/** PUB-UC-6: где и когда пост уже вышел или должен выйти сейчас. */
export function PublicationHistory({ blogId, timezone, publications, onEdit, onDelete, pending = false, editing = null }: PublicationHistoryProps): ReactElement {
  const { t } = useT();
  if (publications.length === 0) return <p className="u-muted">{t('publishing.history.empty')}</p>;
  return (
    <ul className={styles.list}>
      {publications.map((publication) => {
        const published = publication.status === PublicationStatus.Published;
        const isEditing = editing?.publicationId === publication.id;
        return (
          <li key={publication.id} className={styles.item}>
            <PublicationLine
              publication={publication}
              timezone={timezone}
              actions={
                publication.status === PublicationStatus.Due ? (
                  <Link to="publication" params={{ blogId, publicationId: publication.id }}>
                    {t('publishing.history.openPackage')}
                  </Link>
                ) : published ? (
                  <>
                    {publication.externalUrl ? (
                      <a href={publication.externalUrl} target="_blank" rel="noreferrer noopener">
                        {t('publishing.history.url')}
                      </a>
                    ) : null}
                    {onEdit && !isEditing ? (
                      <IconButton icon="edit" size="sm" label={t('publishing.history.edit')} disabled={pending} onClick={() => onEdit(publication)} />
                    ) : null}
                    {onDelete ? <IconButton icon="trash" size="sm" label={t('publishing.history.delete')} disabled={pending} onClick={() => onDelete(publication)} /> : null}
                  </>
                ) : null
              }
            />
            {publication.note ? <span className="u-muted u-small">{publication.note === WITHDRAWN_NOTE ? t('publishing.history.withdrawn') : publication.note}</span> : null}
            {isEditing ? <div className={styles.editor}>{editing.form}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}
